/**
 * recurringEventUtils.js
 * Pure helpers for the "Recurring Events" dock scheduling feature: determining
 * whether a recurring event definition is active on a given date, detecting
 * overlaps between recurring events, and filtering appointment slots against
 * recurring-event blocked windows.
 *
 * All four recurrence patterns (DAILY/WEEKLY/MONTHLY/YEARLY) repeat within a
 * single calendar year, so a bounded day-by-day scan is enough to prove two
 * open-ended definitions can (or can't) ever coincide — no closed-form
 * pattern-intersection math needed.
 */

// ~2 years: a safe margin over the 366 days needed for annual periodicity.
// Known limitation: does not fully cover a YEARLY Feb-29 pattern, which only
// realigns with itself every 4 years.
export const MAX_SCAN_DAYS = 730;

const DAY_NAMES = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseYMD = (dateYMD) => {
  const [y, m, d] = dateYMD.split("-").map(Number);
  return { y, m, d };
};

const toUtcMs = (dateYMD) => {
  const { y, m, d } = parseYMD(dateYMD);
  return Date.UTC(y, m - 1, d);
};

const toYMD = (utcMs) => {
  const date = new Date(utcMs);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * UTC ms of the Monday starting the week containing the date. "Every N weeks"
 * must bucket on calendar weeks (the app's calendar is Monday-first, firstDay=1),
 * not on the raw effective weekday — otherwise a multi-day selection like
 * Mon & Fri from a Tuesday effective date straddles a bucket boundary and the
 * two days never fall in the same calendar week. Interval 1 is unaffected.
 * Mirrors the backend's CalendarDateMatcher#weeksBetweenAligned.
 */
const mondayOfWeekUtcMs = (dateYMD) => {
  const ms = toUtcMs(dateYMD);
  const daysSinceMonday = (new Date(ms).getUTCDay() + 6) % 7; // 0=Sun..6=Sat -> Mon=0
  return ms - daysSinceMonday * MS_PER_DAY;
};

const toMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return (Number.isNaN(h) ? 0 : h) * 60 + (Number.isNaN(m) ? 0 : m);
};

/** Returns the entry's blocked time window in minutes-from-midnight. */
export const getTimeWindowMinutes = (entry) => {
  if (entry.allDay) return { startMin: 0, endMin: 1440 };
  return { startMin: toMinutes(entry.startTime), endMin: toMinutes(entry.endTime) };
};

/**
 * Matches a Monthly "Nth weekday" recurrence, e.g. "1st Monday" (weekOfMonth=1)
 * or "Last Friday" (weekOfMonth=-1) of the month. Mirrors the backend's
 * CalendarDateMatcher#matchesNthWeekdayOfMonth exactly.
 */
const matchesNthWeekdayOfMonth = (y, m, d, dayOfWeekName, weekOfMonth) => {
  const target = DAY_NAMES.indexOf((dayOfWeekName || "").toUpperCase());
  if (target === -1) return false;
  if (new Date(Date.UTC(y, m - 1, d)).getUTCDay() !== target) return false;
  if (weekOfMonth === -1) {
    const next = new Date(Date.UTC(y, m - 1, d + 7));
    return next.getUTCMonth() !== m - 1;
  }
  const occurrence = Math.floor((d - 1) / 7) + 1;
  return occurrence === weekOfMonth;
};

/**
 * Whether a recurring event definition is active on the given date, based on
 * its recurrence pattern, "every N [unit]" interval, and effective/end date
 * bounds. Interval math mirrors the backend's ChronoUnit-based checks exactly
 * (day-count/7-day-bucket/calendar-month-count/year-count between the
 * effective date and the candidate date) so client-side overlap detection and
 * slot filtering agree with what the server will actually schedule.
 */
export const isRecurringEventActiveOnDate = (entry, dateYMD) => {
  if (!entry?.effectiveDate || !dateYMD) return false;
  if (dateYMD < entry.effectiveDate) return false;
  if (entry.endDate && dateYMD > entry.endDate) return false;

  const interval = entry.intervalCount && entry.intervalCount > 0 ? entry.intervalCount : 1;
  const { y, m, d } = parseYMD(dateYMD);
  const eff = parseYMD(entry.effectiveDate);

  switch (entry.recurrencePattern) {
    case "DAILY": {
      const daysBetween = Math.round((toUtcMs(dateYMD) - toUtcMs(entry.effectiveDate)) / MS_PER_DAY);
      return daysBetween % interval === 0;
    }
    case "WEEKLY": {
      const dayName = DAY_NAMES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
      if (!Array.isArray(entry.daysOfWeek) || !entry.daysOfWeek.includes(dayName)) return false;
      const weeksBetween = Math.round(
        (mondayOfWeekUtcMs(dateYMD) - mondayOfWeekUtcMs(entry.effectiveDate)) / (7 * MS_PER_DAY)
      );
      return weeksBetween % interval === 0;
    }
    case "MONTHLY": {
      const monthsBetween = (y - eff.y) * 12 + (m - eff.m);
      if (monthsBetween % interval !== 0) return false;
      // monthlyMode decides when it is set. A new row keeps its default weekOfMonth and
      // monthlyDayOfWeek in day-of-month mode, so treating those fields as a signal would
      // read "day 15" as "1st Monday". Saved rows get monthlyMode from the same weekOfMonth
      // test the backend uses (dockSettingsParser).
      const isNthWeekday = entry.monthlyMode
        ? entry.monthlyMode === "DAY_OF_WEEK"
        : entry.weekOfMonth != null && entry.monthlyDayOfWeek;
      if (isNthWeekday) {
        return matchesNthWeekdayOfMonth(y, m, d, entry.monthlyDayOfWeek, entry.weekOfMonth);
      }
      return d === entry.dayOfMonth;
    }
    case "YEARLY": {
      if (m !== entry.monthOfYear || d !== entry.dayOfMonth) return false;
      // Years are counted from the first occurrence, which is the start date the
      // payload builder sends, so a draft and its saved copy agree with the backend.
      const first = firstYearlyOccurrenceOnOrAfter(entry.effectiveDate, entry.monthOfYear, entry.dayOfMonth);
      if (!first) return false;
      return (y - parseYMD(first).y) % interval === 0;
    }
    default:
      return false;
  }
};

/**
 * First date on or after `effectiveDate` that falls on monthOfYear/dayOfMonth, or null
 * when that month/day never exists (e.g. April 31). Feb 29 resolves to the next leap year.
 *
 * The backend's yearly matcher (CalendarDateMatcher) takes the month from the row's
 * startDate and ignores monthOfYear. A Yearly row whose start date sits in another month
 * would close the wrong day, so the payload starts it on its first real occurrence.
 */
export const firstYearlyOccurrenceOnOrAfter = (effectiveDate, monthOfYear, dayOfMonth) => {
  if (!effectiveDate || !monthOfYear || !dayOfMonth) return null;
  const { y } = parseYMD(effectiveDate);
  for (let year = y; year <= y + 8; year++) {
    const ms = Date.UTC(year, monthOfYear - 1, dayOfMonth);
    // Date.UTC rolls an impossible day into the next month (April 31 -> May 1).
    if (new Date(ms).getUTCMonth() !== monthOfYear - 1) continue;
    const ymd = toYMD(ms);
    if (ymd >= effectiveDate) return ymd;
  }
  return null;
};

/** Half-open interval overlap — touching boundaries (e.g. 14:00-15:00 vs 15:00-16:00) don't count. */
export const timeRangesOverlap = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && bStart < aEnd;

/**
 * Scans a bounded window of dates to determine whether two recurring event
 * definitions can ever be active on the same date with overlapping time
 * windows. Returns the first conflicting date found, if any.
 */
export const findRecurringEventOverlap = (entryA, entryB, { horizonDays = MAX_SCAN_DAYS } = {}) => {
  const { startMin: aStart, endMin: aEnd } = getTimeWindowMinutes(entryA);
  const { startMin: bStart, endMin: bEnd } = getTimeWindowMinutes(entryB);
  // The time windows are the same every day, so if they never overlap, no date can
  // conflict. Checking this first avoids a 730-day scan for most pairs.
  if (!timeRangesOverlap(aStart, aEnd, bStart, bEnd)) return { overlaps: false };

  const scanStartYMD = entryA.effectiveDate > entryB.effectiveDate ? entryA.effectiveDate : entryB.effectiveDate;
  const scanStartMs = toUtcMs(scanStartYMD);

  const capMsCandidates = [scanStartMs + horizonDays * MS_PER_DAY];
  if (entryA.endDate) capMsCandidates.push(toUtcMs(entryA.endDate));
  if (entryB.endDate) capMsCandidates.push(toUtcMs(entryB.endDate));
  const scanEndMs = Math.min(...capMsCandidates);

  if (scanStartMs > scanEndMs) return { overlaps: false };

  for (let ms = scanStartMs; ms <= scanEndMs; ms += MS_PER_DAY) {
    const dateYMD = toYMD(ms);
    if (isRecurringEventActiveOnDate(entryA, dateYMD) && isRecurringEventActiveOnDate(entryB, dateYMD)) {
      return { overlaps: true, date: dateYMD };
    }
  }

  return { overlaps: false };
};

/**
 * Validates that no two recurring events overlap. Pass `candidateIndex` to
 * only check that entry against the others (cheaper, and gives clearer
 * per-row error attribution than a full re-validation on every keystroke).
 */
export const validateNoOverlappingRecurringEvents = (recurringEvents, candidateIndex) => {
  const conflicts = [];
  const hasCandidate = typeof candidateIndex === "number";

  for (let i = 0; i < recurringEvents.length; i++) {
    for (let j = i + 1; j < recurringEvents.length; j++) {
      if (hasCandidate && candidateIndex !== i && candidateIndex !== j) continue;
      const { overlaps, date } = findRecurringEventOverlap(recurringEvents[i], recurringEvents[j]);
      if (overlaps) conflicts.push({ indexA: i, indexB: j, date });
    }
  }

  return { valid: conflicts.length === 0, conflicts };
};

/** Returns the blocked minute-windows active on a single date (no scanning — O(n) lookup). */
export const getBlockedWindowsForDate = (recurringEvents, dateYMD) =>
  (recurringEvents || [])
    .filter((entry) => isRecurringEventActiveOnDate(entry, dateYMD))
    .map((entry) => ({
      ...getTimeWindowMinutes(entry),
      eventName: entry.eventName,
      recurringEventId: entry.recurringEventId,
    }));

/**
 * Drops (does not trim) any slot whose window overlaps a recurring-event
 * blocked window on the given date.
 */
export const filterSlotsAgainstRecurringEvents = (slots, recurringEvents, dateYMD) => {
  const windows = getBlockedWindowsForDate(recurringEvents, dateYMD);
  if (!windows.length) return slots;

  return (slots || []).filter((slot) => {
    const slotStart = toMinutes(slot.startTime);
    const slotEnd = toMinutes(slot.endTime);
    return !windows.some((w) => timeRangesOverlap(slotStart, slotEnd, w.startMin, w.endMin));
  });
};

/**
 * A row the user added in the settings UI that has not been saved yet. Saved rows
 * carry the backend eventId as recurringEventId (see dockSettingsParser).
 */
export const isUnsavedRecurringEvent = (entry) => entry?.recurringEventId === undefined;

/**
 * "Ends on a date" was chosen but no date picked yet. Previously a blank end date
 * silently meant "recurs indefinitely", so there was nothing to validate; now that
 * indefinite is an explicit choice, a blank date is an unfinished one.
 */
export const hasIncompleteEndDate = (entry) => entry?.endMode === "ON_DATE" && !entry.endDate;

/** Builds the /api/events request body for one recurring event on one calendar. */
export const buildRecurringEventPayload = (re, calendarId) => {
  const isMonthlyByWeekday = re.recurrencePattern === "MONTHLY" && re.monthlyMode === "DAY_OF_WEEK";
  const startDate =
    re.recurrencePattern === "YEARLY"
      ? firstYearlyOccurrenceOnOrAfter(re.effectiveDate, re.monthOfYear, re.dayOfMonth) || re.effectiveDate
      : re.effectiveDate;
  return {
    ...(re.recurringEventId !== undefined ? { eventId: re.recurringEventId } : {}),
    eventDescription: re.eventName || "Recurring Event",
    eventStatueType: "CLOSE",
    eventRecurringType: re.recurrencePattern,
    isCancelled: false,
    isWholeDay: re.allDay,
    startDate,
    // A null end date is what the backend stores for "recurs indefinitely".
    endDate: re.endMode === "ON_DATE" ? re.endDate || null : null,
    // The frontend always sends the full current row on update — this tells the
    // shared bulk-update endpoint (whose other callers send sparse partial
    // payloads) to actually clear fields that come through null/empty, rather
    // than leaving them at whatever the row previously had (e.g. switching
    // Monthly from day-of-week back to day-of-month must clear weekOfMonth).
    fullOverwrite: true,
    startTime: re.allDay ? null : `${re.startTime}:00`,
    endTime: re.allDay ? null : `${re.endTime}:00`,
    daysOfWeek: re.recurrencePattern === "WEEKLY" ? re.daysOfWeek : undefined,
    dayOfWeek: isMonthlyByWeekday ? re.monthlyDayOfWeek : undefined,
    weekOfMonth: isMonthlyByWeekday ? re.weekOfMonth : undefined,
    dayOfMonth:
      (re.recurrencePattern === "MONTHLY" && !isMonthlyByWeekday) || re.recurrencePattern === "YEARLY"
        ? re.dayOfMonth
        : undefined,
    monthOfYear: re.recurrencePattern === "YEARLY" ? re.monthOfYear : undefined,
    intervalCount: re.intervalCount && re.intervalCount > 0 ? re.intervalCount : 1,
    calendarId,
  };
};
